"use client";
/**
 * Zwaluw — Werkwijze 5-stappen timeline
 *
 * Sticky rail-navigatie links (5 stappen genummerd 01–05) en grote stap-
 * cards rechts met titel, body, meta-tekst en foto. IntersectionObserver
 * houdt de actieve rail-item in sync met de scroll-positie.
 *
 * Wordt gebruikt op /werkwijze en de homepage. CSS in styles-v2.css onder
 * .ww-tl-rail / .ww-tl-step.
 */
import * as React from "react";

const ClockSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const PinSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const FileSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
  </svg>
);
const CheckSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);
const CubeSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5 12 2" />
  </svg>
);
const HouseSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);
const CheckCircleSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path fill="#fff" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" stroke="none" />
  </svg>
);

export interface WerkwijzeStep {
  num: string;
  railLabel: string;
  titleBefore: string;
  titleEm: string;
  titleAfter?: string;
  body: string;
  metaIcon: React.ReactNode;
  metaText: string;
  photo: string;
  photoAlt: string;
  capIcon: React.ReactNode;
  capText: string;
}

const DEFAULT_STEPS: WerkwijzeStep[] = [
  {
    num: "01",
    railLabel: "Brochure aanvragen",
    titleBefore: "Brochure ",
    titleEm: "aanvragen.",
    body: "U vult uw gegevens in en ontvangt direct de digitale brochure. Een papieren versie volgt per post — handig om rustig samen door te bladeren.",
    metaIcon: ClockSvg,
    metaText: "Op werkdagen besteld, morgen in huis",
    photo: "/zwaluw/brochure-handen.jpg",
    photoAlt: "Badkamer-brochure in handen",
    capIcon: FileSvg,
    capText: "Gratis · 48 pagina's",
  },
  {
    num: "02",
    railLabel: "Adviesgesprek bij u thuis",
    titleBefore: "Gratis ",
    titleEm: "adviesgesprek",
    titleAfter: " bij u thuis.",
    body: "Onze adviseur komt op afspraak langs en bespreekt rustig uw wensen, situatie en mogelijkheden. Geen verkooppraatje — alleen eerlijk advies wat bij u past.",
    metaIcon: ClockSvg,
    metaText: "Duurt 1 tot 2 uur",
    photo: "/zwaluw/thuisadvies-adviseur.jpg",
    photoAlt: "Adviseur bij u thuis",
    capIcon: PinSvg,
    capText: "Bij u thuis, geheel kosteloos",
  },
  {
    num: "03",
    railLabel: "3D-ontwerp + inmeten",
    titleBefore: "3D-ontwerp ",
    titleEm: "+ inmeten",
    titleAfter: " tijdens het adviesgesprek.",
    body: "Tijdens hetzelfde huisbezoek nemen we direct de definitieve maten op en maken we een 3D-visualisatie van uw nieuwe douche of badkamer. Inclusief vaste prijs zonder verrassingen achteraf — u ziet vooraf precies hoe het wordt.",
    metaIcon: ClockSvg,
    metaText: "Direct tijdens het adviesgesprek",
    photo: "/zwaluw/werkwijze-3d-ontwerp.jpg",
    photoAlt: "3D-ontwerp voorbeeld",
    capIcon: CubeSvg,
    capText: "3D-render + vaste totaalprijs",
  },
  {
    num: "04",
    railLabel: "Montage",
    titleBefore: "Montage in ",
    titleEm: "1 tot 5 dagen.",
    body: "Een Comfortdouche staat in één werkdag, een Pure Instapbad ook. Een totale renovatie duurt 3 tot 5 werkdagen — u kunt blijven wonen.",
    metaIcon: HouseSvg,
    metaText: "Zonder uitwijken — u blijft thuis wonen",
    photo: "/zwaluw/werkwijze-montage.jpg",
    photoAlt: "Eigen monteurs aan het werk",
    capIcon: CheckSvg,
    capText: "Eigen montage team",
  },
  {
    num: "05",
    railLabel: "Bezemschoon opgeleverd",
    titleBefore: "Bezemschoon ",
    titleEm: "opgeleverd.",
    body: "Onze monteurs ruimen alles op en nemen het oude materiaal mee. De volgende dag kunt u alweer gewoon douchen.",
    metaIcon: CheckSvg,
    metaText: "Inclusief afvoer oude badkamer",
    photo: "/zwaluw/werkwijze-oplevering.jpg",
    photoAlt: "Bezemschoon opgeleverde douche",
    capIcon: CheckCircleSvg,
    capText: "Volgende dag weer douchen",
  },
];

export interface WerkwijzeTimelineProps {
  eyebrow?: string;
  headline?: string;
  headlineEm?: string;
  intro?: string;
  steps?: WerkwijzeStep[];
  /** Toon de mid-CTA (brochure + thuisadvies) onder de stappen */
  showMidCta?: boolean;
}

const ARROW_ICON = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

export function WerkwijzeTimeline({
  eyebrow = "In 5 stappen",
  headline = "Van brochure tot",
  headlineEm = "uw eerste comfort-douche.",
  intro = "Alles wat er gebeurt vanaf het moment dat u contact opneemt — geen verborgen kosten, geen vage planningen.",
  steps = DEFAULT_STEPS,
  showMidCta = true,
}: WerkwijzeTimelineProps) {
  const [activeStep, setActiveStep] = React.useState(0);
  const stepRefs = React.useRef<(HTMLElement | null)[]>([]);

  React.useEffect(() => {
    if (!("IntersectionObserver" in window)) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const idx = stepRefs.current.indexOf(e.target as HTMLElement);
            if (idx !== -1) setActiveStep(idx);
          }
        });
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: 0 }
    );
    stepRefs.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollToStep = (i: number) => {
    stepRefs.current[i]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="ww-timeline">
      <div className="zw-container">
        <div className="ww-timeline-head">
          <span className="zw-eyebrow">{eyebrow}</span>
          <h2>
            {headline} <em>{headlineEm}</em>
          </h2>
          {intro ? <p>{intro}</p> : null}
        </div>

        <div className="ww-tl-grid">
          <nav className="ww-tl-rail" aria-label="Stappen">
            {steps.map((s, i) => (
              <button
                key={i}
                className={`ww-tl-rail-item${i === activeStep ? " active" : ""}`}
                onClick={() => scrollToStep(i)}
                type="button"
              >
                <span className="ww-tl-rail-num">{s.num}</span> {s.railLabel}
              </button>
            ))}
          </nav>

          <div className="ww-tl-steps">
            {steps.map((s, i) => (
              <article
                key={i}
                className="ww-tl-step"
                id={`step-${i + 1}`}
                ref={(el) => {
                  stepRefs.current[i] = el;
                }}
              >
                <div className="ww-tl-step-text">
                  <span className="ww-tl-step-num">Stap {s.num}</span>
                  <h3>
                    {s.titleBefore}
                    <em>{s.titleEm}</em>
                    {s.titleAfter ?? ""}
                  </h3>
                  <p>{s.body}</p>
                  <span className="ww-tl-step-meta">
                    {s.metaIcon}
                    {s.metaText}
                  </span>
                </div>
                <div className="ww-tl-step-photo">
                  <img
                    src={s.photo}
                    alt={s.photoAlt}
                    width={1600}
                    height={1200}
                    loading="lazy"
                    style={{ width: "100%", height: "auto", display: "block" }}
                  />
                  <span className="ww-tl-step-photo-cap">
                    {s.capIcon}
                    {s.capText}
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>

        {showMidCta ? (
          <div className="ww-mid-cta">
            <div className="ww-mid-cta-text">
              <h3>
                Eerst de brochure, dan een <em>3D-ontwerp</em> bij u thuis?
              </h3>
              <p>
                Begin met de gratis brochure of plan direct een adviesgesprek —
                geheel vrijblijvend.
              </p>
            </div>
            <div className="ww-mid-cta-actions">
              <a href="/gratis-brochure" className="zw-btn zw-btn-primary">
                Brochure aanvragen
                {ARROW_ICON}
              </a>
              <a href="/thuisadvies" className="zw-btn zw-btn-ghost">
                Plan adviesgesprek
              </a>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default WerkwijzeTimeline;
